import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import { buildApiUrl } from 'src/utils/api';

const getIdTokenOrThrow = async () => {
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error('Authentication is still loading. Please retry.');
  }

  return user.getIdToken();
};

const authConfig = async () => {
  const idToken = await getIdTokenOrThrow();
  return {
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  };
};

export const requestSmsMarketingSettings = createAsyncThunk('smsMarketing/requestSettings', async () => {
  const response = await axios.get(buildApiUrl('/api/sms-marketing/settings'), await authConfig());
  return response.data;
});

export const createSmsMarketingAutomation = createAsyncThunk('smsMarketing/createAutomation', async (payload) => {
  const response = await axios.post(buildApiUrl('/api/sms-marketing/automations'), payload, await authConfig());
  return response.data;
});

export const updateSmsMarketingAutomation = createAsyncThunk('smsMarketing/updateAutomation', async ({ automationId, ...payload }) => {
  const response = await axios.put(buildApiUrl(`/api/sms-marketing/automations/${automationId}`), payload, await authConfig());
  return response.data;
});

export const deleteSmsMarketingAutomation = createAsyncThunk('smsMarketing/deleteAutomation', async (automationId) => {
  const response = await axios.delete(buildApiUrl(`/api/sms-marketing/automations/${automationId}`), await authConfig());
  return response.data;
});

export const sendSmsMarketingTestMessage = createAsyncThunk('smsMarketing/sendTestMessage', async ({ automationId, phone }) => {
  const response = await axios.post(
    buildApiUrl(`/api/sms-marketing/automations/${automationId}/test`),
    { phone },
    await authConfig()
  );
  return response.data;
});

export const requestSmsMarketingAnalytics = createAsyncThunk('smsMarketing/requestAnalytics', async () => {
  const response = await axios.get(buildApiUrl('/api/sms-marketing/analytics'), await authConfig());
  return response.data;
});

export const requestSmsMarketingJobs = createAsyncThunk('smsMarketing/requestJobs', async (status = 'pending') => {
  const response = await axios.get(buildApiUrl(`/api/sms-marketing/jobs?status=${encodeURIComponent(status)}`), await authConfig());
  return response.data;
});

export const cancelSmsMarketingJob = createAsyncThunk('smsMarketing/cancelJob', async (jobId) => {
  const response = await axios.post(buildApiUrl(`/api/sms-marketing/jobs/${jobId}/cancel`), {}, await authConfig());
  return response.data;
});

export const requestSmsMarketingBlacklist = createAsyncThunk('smsMarketing/requestBlacklist', async () => {
  const response = await axios.get(buildApiUrl('/api/sms-marketing/blacklist'), await authConfig());
  return response.data;
});

export const addSmsMarketingBlacklistEntry = createAsyncThunk('smsMarketing/addBlacklistEntry', async (payload) => {
  const response = await axios.post(buildApiUrl('/api/sms-marketing/blacklist'), payload, await authConfig());
  return response.data;
});

export const removeSmsMarketingBlacklistEntry = createAsyncThunk('smsMarketing/removeBlacklistEntry', async (entryId) => {
  const response = await axios.delete(buildApiUrl(`/api/sms-marketing/blacklist/${entryId}`), await authConfig());
  return response.data;
});

const initialState = {
  loading: false,
  saving: false,
  error: null,
  capabilities: null,
  triggerCatalog: {
    sources: [],
    events: []
  },
  delayUnits: [],
  variables: [],
  automations: [],
  analytics: {
    totals: {},
    by_automation: []
  },
  queuedJobs: [],
  blacklist: [],
  queueLoading: false,
  blacklistLoading: false
};

const hydrate = (state, payload) => {
  state.capabilities = payload.capabilities || null;
  state.triggerCatalog = payload.trigger_catalog || { sources: [], events: [] };
  state.delayUnits = payload.delay_units || [];
  state.variables = payload.variables || [];
  state.automations = payload.automations || [];
  state.analytics = payload.analytics || state.analytics;
};

const slice = createSlice({
  name: 'smsMarketing',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(requestSmsMarketingSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestSmsMarketingSettings.fulfilled, (state, action) => {
        state.loading = false;
        hydrate(state, action.payload);
      })
      .addCase(requestSmsMarketingSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Unable to load SMS automations.';
      })
      .addCase(createSmsMarketingAutomation.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createSmsMarketingAutomation.fulfilled, (state, action) => {
        state.saving = false;
        hydrate(state, action.payload);
      })
      .addCase(createSmsMarketingAutomation.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Unable to create SMS automation.';
      })
      .addCase(updateSmsMarketingAutomation.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSmsMarketingAutomation.fulfilled, (state, action) => {
        state.saving = false;
        hydrate(state, action.payload);
      })
      .addCase(updateSmsMarketingAutomation.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Unable to update SMS automation.';
      })
      .addCase(deleteSmsMarketingAutomation.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteSmsMarketingAutomation.fulfilled, (state, action) => {
        state.saving = false;
        hydrate(state, action.payload);
      })
      .addCase(deleteSmsMarketingAutomation.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Unable to delete SMS automation.';
      })
      .addCase(sendSmsMarketingTestMessage.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(sendSmsMarketingTestMessage.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(sendSmsMarketingTestMessage.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Unable to send test SMS.';
      })
      .addCase(requestSmsMarketingAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload.analytics || state.analytics;
      })
      .addCase(requestSmsMarketingAnalytics.rejected, (state, action) => {
        state.error = action.error.message || 'Unable to load SMS automation analytics.';
      })
      .addCase(requestSmsMarketingJobs.pending, (state) => {
        state.queueLoading = true;
      })
      .addCase(requestSmsMarketingJobs.fulfilled, (state, action) => {
        state.queueLoading = false;
        state.queuedJobs = action.payload.jobs || [];
      })
      .addCase(requestSmsMarketingJobs.rejected, (state, action) => {
        state.queueLoading = false;
        state.error = action.error.message || 'Unable to load queued SMS messages.';
      })
      .addCase(cancelSmsMarketingJob.fulfilled, (state, action) => {
        state.queuedJobs = state.queuedJobs.filter((job) => Number(job.id) !== Number(action.payload.job?.id));
      })
      .addCase(cancelSmsMarketingJob.rejected, (state, action) => {
        state.error = action.error.message || 'Unable to cancel queued SMS message.';
      })
      .addCase(requestSmsMarketingBlacklist.pending, (state) => {
        state.blacklistLoading = true;
      })
      .addCase(requestSmsMarketingBlacklist.fulfilled, (state, action) => {
        state.blacklistLoading = false;
        state.blacklist = action.payload.blacklist || [];
      })
      .addCase(requestSmsMarketingBlacklist.rejected, (state, action) => {
        state.blacklistLoading = false;
        state.error = action.error.message || 'Unable to load SMS blacklist.';
      })
      .addCase(addSmsMarketingBlacklistEntry.fulfilled, (state, action) => {
        state.blacklist = action.payload.blacklist || state.blacklist;
        state.queuedJobs = action.payload.jobs || state.queuedJobs;
      })
      .addCase(addSmsMarketingBlacklistEntry.rejected, (state, action) => {
        state.error = action.error.message || 'Unable to blacklist SMS recipient.';
      })
      .addCase(removeSmsMarketingBlacklistEntry.fulfilled, (state, action) => {
        state.blacklist = action.payload.blacklist || state.blacklist;
      })
      .addCase(removeSmsMarketingBlacklistEntry.rejected, (state, action) => {
        state.error = action.error.message || 'Unable to remove SMS blacklist entry.';
      });
  }
});

export const reducer = slice.reducer;
